import {
    AiImagePresetStyles,
    AiMessage,
    GenerationFile,
    OutlineQuestion,
    OutlineQuestionAnswer,
    SlideSuggestion
} from "common/aiConstants";
import { IPresentation, IPresentationStorageModel, ISlide, IStorageModel } from "common/interfaces";

import { AmplitudeCategories, trackEvent } from "./amplitude";

export function GeneratePresentation({ presentation: _presentation }: { presentation: IPresentation | IPresentationStorageModel }) {
    const presentation: IPresentation = ("get" in _presentation) ? _presentation.attributes : _presentation;

    const metadata = presentation.metadata ?? {};

    const isPresetImageStyle = AiImagePresetStyles.some(style => style.style === metadata.imageStyle);

    const props = {
        ..._getFilesProps(metadata.files ?? []),
        presentation_id: presentation.id,
        num_slides: Object.keys(presentation.slideRefs).length,
        from_tour: false,
        used_ai_images: metadata.imageSource === "ai",
        model_name: metadata.imageModel,
        image_quality: metadata.imageQuality,
        custom_style_type: isPresetImageStyle ? "preset" : "prompt",
        custom_style_type_prompt: isPresetImageStyle ? null : metadata.imageStyle,
        image_style: isPresetImageStyle ? metadata.imageStyle : "custom",
        language: metadata.language,
        prompt: metadata.prompt,
        preserve_exact_text: metadata.preserveExactText,
        web_search: metadata.webSearch
    };

    trackEvent(AmplitudeCategories.Presentation, "Generate", props);
}

function _getFilesProps(files: GenerationFile[]) {
    const usedFiles = files.filter(file => file.hasBeenUsed);
    return {
        files: usedFiles.map(({ rawFileName, parsedFileName, hasBeenUsedFor }) => ({ rawFileName, parsedFileName, hasBeenUsedFor })),
        files_count: usedFiles.length
    };
}

export function GeneratePresentationOutline({ prompt, files, webSearch }: { prompt: string, files: GenerationFile[], webSearch: boolean }) {
    const props = {
        ..._getFilesProps(files),
        prompt: prompt,
        web_search: webSearch
    };

    trackEvent(AmplitudeCategories.PresentationOutline, "Generate", props);
}

export function RegeneratePresentationOutline({ prompt, files, webSearch }: { prompt: string, files: GenerationFile[], webSearch: boolean }) {
    const props = {
        ..._getFilesProps(files),
        prompt: prompt,
        web_search: webSearch
    };

    trackEvent(AmplitudeCategories.PresentationOutline, "Regenerate", props);
}

export function ChatEditPresentationOutline({ prompt, messages, files, webSearch }: { prompt: string, messages: AiMessage[], files: GenerationFile[], webSearch: boolean }) {
    const props = {
        ..._getFilesProps(files),
        prompt: prompt,
        messages: messages.map(({ role, content }) => ({ role, content })),
        web_search: webSearch
    };

    trackEvent(AmplitudeCategories.PresentationOutline, "ChatEdit", props);
}

export function GenerateOutlineQuestions({ prompt, questions }: { prompt: string, questions: OutlineQuestion[] }) {
    trackEvent(AmplitudeCategories.PresentationOutline, "GenerateQuestions", {
        prompt,
        questions_count: questions.length,
        question_ids: questions.map(question => question.id),
        question_priorities: questions.map(question => question.priority)
    });
}

/**
 * The per-question skip and answer rates are the only signal for whether the generated questions
 * are actually worth the user's time, so this is what the questions prompt gets tuned against.
 */
export function AnswerOutlineQuestions({ questions, answers, skipped }: { questions: OutlineQuestion[], answers?: OutlineQuestionAnswer[], skipped: boolean }) {
    const answeredIds = new Set((answers ?? []).map(answer => answer.id));

    trackEvent(AmplitudeCategories.PresentationOutline, "AnswerQuestions", {
        skipped,
        questions_count: questions.length,
        answered_count: answeredIds.size,
        question_ids: questions.map(question => question.id),
        unanswered_question_ids: questions.filter(question => !answeredIds.has(question.id)).map(question => question.id),
        answers: (answers ?? []).map(({ id, values }) => ({ id, values }))
    });
}

export function RefineOutlineFromAnswer({ questionId, values }: { questionId: string, values: string[] }) {
    trackEvent(AmplitudeCategories.PresentationOutline, "RefineFromAnswer", {
        question_id: questionId,
        values
    });
}

function _getSlide(_slide: ISlide | IStorageModel<ISlide>) {
    const slide: ISlide = ("get" in _slide) ? _slide.attributes : _slide;
    return slide;
}

function _getSlideProps(_slide: ISlide | IStorageModel<ISlide>) {
    const slide = _getSlide(_slide);

    const metadata = slide.metadata ?? {};

    const imageSource = metadata.imageSource ?? metadata.generatedModel?.slideImage?.imageSource;
    const imageStyle = metadata.imageStyle ?? metadata.generatedModel?.slideImage?.imageStyle;
    const imageModel = metadata.imageModel ?? metadata.generatedModel?.slideImage?.imageModel;
    const imageQuality = metadata.imageQuality ?? metadata.generatedModel?.slideImage?.imageQuality;
    const isPresetImageStyle = imageStyle ? AiImagePresetStyles.some(style => style.style === imageStyle) : null;

    return {
        ..._getFilesProps(metadata.files ?? []),
        presentation_id: slide.presentationId,
        slide_id: slide.id,
        prompt: metadata.slidePrompt,
        language: metadata.language,
        used_ai_images: imageSource === "ai",
        model_name: imageModel,
        image_quality: imageQuality,
        custom_style_type: imageStyle ? (isPresetImageStyle ? "preset" : "prompt") : null,
        custom_style_type_prompt: imageStyle ? (isPresetImageStyle ? null : imageStyle) : null,
        image_style: imageStyle ? (isPresetImageStyle ? imageStyle : "custom") : null,
        context_source: metadata.files?.length ? "file" : undefined,
        use_presentation_as_context: metadata.presentationAsContext,
        preserve_exact_text: metadata.preserveExactText,
        web_search: metadata.webSearch,
        slide_type: metadata.slideType,
        template_id: slide.template_id
    } as Record<string, any>;
}

export function GenerateSlide({ slide: _slide }: { slide: ISlide | IStorageModel<ISlide> }) {
    const props = _getSlideProps(_slide);
    trackEvent(AmplitudeCategories.Slide, "Generate", props);
}

export function GenerateSlideIdeas({ slide: _slide }: { slide: ISlide | IStorageModel<ISlide> }) {
    const props = _getSlideProps(_slide);
    props.generated_ideas = _getSlide(_slide).metadata?.generatedIdeas?.map(({ slideCategory, slideConfidence, slideReasoning }) => ({ category: slideCategory, confidence: slideConfidence, reasoning: slideReasoning }));
    trackEvent(AmplitudeCategories.Slide, "GenerateIdeas", props);
}

export function SelectSlideIdea({ slide: _slide }: { slide: ISlide | IStorageModel<ISlide> }) {
    const props = _getSlideProps(_slide);
    props.selected_template_id = props.template_id;
    trackEvent(AmplitudeCategories.Slide, "SelectIdea", props);
}

export function SuggestSlideImprovements({
    slide: _slide,
    suggestions
}: {
    slide: ISlide | IStorageModel<ISlide>,
    suggestions: SlideSuggestion[]
}) {
    const props = _getSlideProps(_slide);
    props.suggestions_count = suggestions.length;
    props.suggestions = suggestions.map(({ kind, confidence, action }) => ({
        kind,
        confidence,
        action_type: action.type
    }));
    trackEvent(AmplitudeCategories.Slide, "SuggestImprovements", props);
}

export function ApplySlideSuggestion({
    slide: _slide,
    suggestion,
    layoutFailed
}: {
    slide: ISlide | IStorageModel<ISlide>,
    suggestion: SlideSuggestion,
    layoutFailed: boolean
}) {
    const props = _getSlideProps(_slide);
    props.suggestion_kind = suggestion.kind;
    props.suggestion_action_type = suggestion.action.type;
    props.suggestion_confidence = suggestion.confidence;
    props.layout_failed = layoutFailed;
    trackEvent(AmplitudeCategories.Slide, "ApplySuggestion", props);
}

export function DismissSlideSuggestion({
    slide: _slide,
    suggestion,
    reason
}: {
    slide: ISlide | IStorageModel<ISlide>,
    suggestion: SlideSuggestion,
    reason: string
}) {
    const props = _getSlideProps(_slide);
    props.suggestion_kind = suggestion.kind;
    props.suggestion_action_type = suggestion.action.type;
    props.suggestion_confidence = suggestion.confidence;
    props.dismiss_reason = reason;
    trackEvent(AmplitudeCategories.Slide, "DismissSuggestion", props);
}

export function RegenerateImagePrompt({ slide: _slide, slidePrompt, presentationTopic, generatedImagePrompt }: { slide: ISlide | IStorageModel<ISlide>, slidePrompt: string, presentationTopic: string, generatedImagePrompt: string }) {
    const props = _getSlideProps(_slide);
    props.slide_prompt = slidePrompt;
    props.presentation_topic = presentationTopic;
    props.generated_image_prompt = generatedImagePrompt;
    trackEvent(AmplitudeCategories.Image, "RegeneratePrompt", props);
}
